import { DocumentFileType } from '@risksmart-app/domain/src/types/consts/document-file-type';
import type { InsertDocumentVersionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertDocumentVersionDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import type { FrontendRouterInputs } from '@/hooks/mutations/routerInputs';
import { createMutationHook } from '@/utils/hook-factories';

export type InsertDocumentVersionInput =
  FrontendRouterInputs['documentFile']['insert'];

const toGraphQLVariables = (input: InsertDocumentVersionInput) => {
  const shared = {
    ParentDocumentId: input.ParentDocumentId,
    Version: input.Version,
    Summary: input.Summary,
    ReasonForReview: input.ReasonForReview,
    ReviewedBy: input.ReviewedBy,
    ReviewDate: input.ReviewDate,
    NextReviewDate: input.NextReviewDate,
    CustomAttributeData: input.CustomAttributeData,
  };
  switch (input.Type) {
    case 'file':
      return {
        ...shared,
        Type: DocumentFileType.File,
        FileId: input.FileId,
        Content: null,
        Link: null,
      };
    case 'html':
      return {
        ...shared,
        Type: DocumentFileType.Html,
        FileId: null,
        Content: input.Content,
        Link: null,
      };
    case 'link':
      return {
        ...shared,
        Type: DocumentFileType.Link,
        FileId: null,
        Content: null,
        Link: input.Link,
      };
  }
};

export const useInsertDocumentVersion = createMutationHook({
  name: 'insertDocumentVersion',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.documentFile.insert.mutationOptions({ onSuccess }),
  mapInput: (input: InsertDocumentVersionInput) => input,
  invalidate: (trpc) => [
    trpc.frontend.documentFile.documentFilesByDocumentId.queryKey(),
    trpc.frontend.documentFile.latestDocumentFile.queryKey(),
    trpc.frontend.documentFile.documentFileById.queryKey(),
    trpc.frontend.documentFile.publicDocumentFiles.queryKey(),
    trpc.frontend.documentFile.latestPublicDocumentFileByDocumentId.queryKey(),
    // COI-471 parity: Apollo twin also evicts `document` (a new version
    // changes the parent document's current-version surfaces).
    trpc.frontend.document.pathKey(),
  ],
  mapResponse: (result: { Id: string }): InsertDocumentVersionMutation => ({
    insertDocumentVersion: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertDocumentVersionDocument,
  graphqlVariables: (input: InsertDocumentVersionInput) => ({
    ...toGraphQLVariables(input),
  }),
  evictFields: ['document'],
  refetchQueries: [namedOperations.Query.getDocumentFileById],
});
